import { jwtDecode } from "jwt-decode";
import { getCookie } from "./generalUtilities";

/**
 * Retrieves and decodes the JWT token from the login cookie
 * @template T - The type of the decoded token data
 * @returns The decoded token data
 * @throws Error if no login cookie is found or token cannot be decoded
 */
export const getDecodedToken = <T>(): T => {
  const retrievedToken = getCookie("loginRes");
  if (!retrievedToken) {
    throw new Error("No login cookie found");
  }

  const parsedData = JSON.parse(retrievedToken);
  const decodedToken = jwtDecode<T>(parsedData.accessToken);

  return decodedToken;
};

/**
 * Safely retrieves and decodes the JWT token from the login cookie
 * @template T - The type of the decoded token data
 * @param onError - Optional callback function to handle errors
 * @returns The decoded token data or null if an error occurs
 */
export const getDecodedTokenSafe = <T>(
  onError?: (error: Error) => void,
): T | null => {
  try {
    return getDecodedToken<T>();
  } catch (error) {
    const errorObj =
      error instanceof Error ? error : new Error("Unknown error occurred");
    if (onError) {
      onError(errorObj);
    }
    return null;
  }
};

/**
 * Checks if the JWT token has expired
 * @returns true if the token is expired or invalid, false otherwise
 */
export const isTokenExpired = (): boolean => {
  const decoded = getDecodedTokenSafe<{ exp: number }>();
  if (!decoded || !decoded.exp) {
    return true;
  }
  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp < currentTime;
};
